import { LanguagesEnum } from '@/api/enums';
import { DashboardFilterValue } from '@/client/reports/types';
import {
  FilterableSurveyCategoryQuestion,
  SecurityCultureSurvey,
  SurveyCategory,
  SurveyCategoryEnum,
} from '@/client/security-culture/types';
import { EmptyProductCard } from '@/components/dashboards/admin';
import { SecurityCultureCard } from '@/components/security-culture';
import { DataTable, DataTableColumnType } from '@/components/tables/crud';
import {
  useFilterableCategories,
  useSurveyCategoryRecommendations,
} from '@/hooks/query/security-culture.hooks';
import { useAppSelector } from '@/hooks/store';
import i18n from '@/i18n';
import { LoadingPage } from '@/pages/LoadingPage';
import { selectCurrentAccount } from '@/store/features/account';
import { BarChart } from '@/ui/charts/bar-chart';
import { FlexContainer } from '@/ui/styled-ui';
import { ImportantIcon, ProductType } from '@/utils/helpers';
import { parseFloatingNumber } from '@/utils/helpers/ui.helper';
import { t } from 'i18next';
import { Tooltip } from 'primereact/tooltip';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

const StyledIconWrapper = styled.span`
  display: flex;
  width: 32px;
  height: 32px;
  background: var(--white-main);
  cursor: default;
  justify-content: center;
  align-items: center;
  border-radius: var(--xxsmall-border-radius);
  transition: all 0.4s;

  &:hover {
    background-color: var(--white-main);
  }

  i {
    font-size: 18px;
  }
`;

const FeedbackRow = styled(FlexContainer)`
  justify-content: flex-start;
  gap: var(--small-padding);
  padding-bottom: var(--medium-padding);

  &:not(:last-child) {
    border-bottom: 1px solid var(--gray-dark);
  }

  svg {
    flex-shrink: 0;
    height: var(--default-padding);
    width: var(--default-padding);
  }
  .row-container {
    font-size: var(--small-font-size);
    font-weight: 500;
    line-height: var(--small-line-height);
  }
`;

const ScoreContainer = styled(FlexContainer)`
  .canvas-wrapper {
    position: relative;
    width: 100%;
    &::before {
      position: absolute;
      content: '';
      height: 1px;
      width: 100%;
      bottom: var(--xsmall-padding);
      display: block;
      background-color: var(--gray-dark);
    }
  }
  gap: var(--default-padding);
  align-items: stretch;
  flex-wrap: wrap;
  width: 100%;

  .score {
    flex: 1 1 30%;
    min-width: 362px;
  }
  .questions {
    flex: 1 1 60%;
  }
`;

type SCCategoryProps = {
  filters?: DashboardFilterValue;
  selectedSurveys?: SecurityCultureSurvey[];
};

export const SCCategory: React.FC<SCCategoryProps> = ({
  filters,
  selectedSurveys,
}) => {
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { surveyId, categoryName } = useParams();

  const selectedSurveyIds = useMemo(
    () =>
      selectedSurveys
        ?.map((survey) => survey.id)
        .filter((id): id is string => Boolean(id)),
    [selectedSurveys],
  );

  const routeSurveyIds = useMemo(
    () => surveyId?.split(',').filter(Boolean),
    [surveyId],
  );

  const surveyIdsArray = selectedSurveyIds?.length
    ? selectedSurveyIds
    : routeSurveyIds;

  const isFilterApplied =
    filters && Object.values(filters).some((value) => value?.length > 0);

  const { filteredCategories, isLoading: areFilteredCategoriesLoading } =
    useFilterableCategories({
      accountId: currentAccount?.id,
      surveyId: surveyIdsArray,
      categoryName: categoryName as SurveyCategoryEnum,
      branches: filters?.branches?.map((branch) => branch?.id),
      groups: filters?.groups?.map((group) => group?.id),
      departments: filters?.departments?.map((department) => department?.name),
      managers: filters?.managers?.map((manager) => manager?.name),
      language: i18n.language as LanguagesEnum,
    });

  const { recommendations, isLoading: areRecommendationsLoading } =
    useSurveyCategoryRecommendations({
      accountId: currentAccount?.id,
      surveyId: surveyIdsArray?.[0],
      categoryName: categoryName as SurveyCategoryEnum,
    });

  const transformedCategory: SurveyCategory | undefined = filteredCategories
    ? ({
        id: filteredCategories.category,
        name: filteredCategories.category,
        result: filteredCategories.score,
        previousResult: 0,
        benchmarkResult: filteredCategories.benchmarkScore,
        filteredUsersCount: filteredCategories.participants,
      } as SurveyCategory)
    : undefined;

  const columns: DataTableColumnType[] = [
    {
      field: 'question',
      header: t('security.culture.questions'),
      sortable: false,
      filterable: false,
      render: (row: FilterableSurveyCategoryQuestion) => (
        <span>{row?.questionLabel}</span>
      ),
    },
    {
      field: 'rating',
      header: t('security.culture.target.group'),
      sortable: false,
      filterable: false,
      className: 'text-center',
      headerClassName: 'text-center',
      render: (row: FilterableSurveyCategoryQuestion) =>
        parseFloatingNumber(row.score),
    },
  ];

  if (areFilteredCategoriesLoading || areRecommendationsLoading) {
    return <LoadingPage message={t('security.culture.survey.loading')} />;
  }

  const participantsCount = filteredCategories?.participants;

  if (typeof participantsCount === 'number' && participantsCount < 5) {
    return (
      <EmptyProductCard
        type={ProductType.SECURITY_CULTURE_DASHBOARD}
        id="empty-security-culture-dashboard"
      />
    );
  }

  return (
    <FlexContainer direction="column" align="flex-start">
      <ScoreContainer>
        <SecurityCultureCard
          id="sc-category-score"
          className="score"
          title={t('security.culture.score.bars')}
          content={
            <>
              {transformedCategory ? (
                <BarChart
                  id="category-chart"
                  value={parseFloatingNumber(transformedCategory?.result)}
                  secondaryValue={parseFloatingNumber(
                    transformedCategory?.benchmarkResult,
                  )}
                  displayXAxis={false}
                  height="154"
                  defaultTooltip={t('security.culture.target.group')}
                  benchmarkTooltip={t('security.culture.other.organisations')}
                  isPurple
                />
              ) : (
                <p className="mx-auto">{t('security.culture.no.data')}</p>
              )}
            </>
          }
        />

        <SecurityCultureCard
          id="sc-category-questions"
          className="questions"
          title={t('security.culture.surveyQuestions')}
          titleAddition={
            <>
              <StyledIconWrapper id="security-culture-questions">
                <i className="pi pi-info-circle" />
              </StyledIconWrapper>
              <Tooltip
                target="#security-culture-questions"
                position="bottom"
                content={
                  isFilterApplied
                    ? t('sc.dashboard.comments.only.for.full.measurement')
                    : t('security.culture.surveyQuestions.tooltip')
                }
              />
            </>
          }
          content={
            <DataTable
              classNames="simple-datatable security-culture questions"
              data={filteredCategories?.questions || []}
              count={filteredCategories?.questions?.length || 0}
              isLoading={areFilteredCategoriesLoading}
              columns={columns}
            />
          }
        />
      </ScoreContainer>
      <SecurityCultureCard
        id="sc-category-recommendations"
        title={t('security.culture.whatCanBeImproved')}
        titleAddition={
          <>
            <StyledIconWrapper id="security-culture-recommendations">
              <i className="pi pi-info-circle" />
            </StyledIconWrapper>
            <Tooltip
              target="#security-culture-recommendations"
              position="bottom"
              content={t('security.culture.whatCanBeImproved.tooltip')}
            />
          </>
        }
        content={
          recommendations?.count ? (
            <>
              {recommendations.result.map((recommendation) => (
                <FeedbackRow key={recommendation.id}>
                  <ImportantIcon />
                  <div className="row-container">
                    <div className="font-bold">{recommendation.title}:</div>
                    <div>{recommendation.description}</div>
                  </div>
                </FeedbackRow>
              ))}
            </>
          ) : (
            <FeedbackRow>{t('filter.noItems')}</FeedbackRow>
          )
        }
      />
    </FlexContainer>
  );
};
